import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, Phone, TrendingUp, TrendingDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";

function CountUp({ to, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1600;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setVal(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString("en-IN", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}{suffix}</span>;
}

const SPARK = "M0,62 C20,58 32,60 48,52 C64,44 76,48 92,40 C108,32 122,36 140,26 C158,16 172,22 190,12 C200,7 210,9 220,4";

const KEYWORDS = [
  { kw: "seo services india", pos: 4, delta: +6 },
  { kw: "technical seo audit", pos: 3, delta: +11 },
  { kw: "shopify seo expert", pos: 7, delta: +14 },
  { kw: "local seo company", pos: 9, delta: +4 },
];

function Dashboard() {
  return (
    <div className="relative rounded-2xl border border-rw-border2 bg-rw-card/90 backdrop-blur p-5 sm:p-6 glow-green" data-testid="hero-dashboard">
      <div className="flex items-center justify-between border-b border-rw-border pb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rw-blue">SEO Intelligence</p>
          <p className="font-jakarta text-sm font-bold text-rw-text mt-1">Organic Growth Dashboard</p>
        </div>
        <span className="rounded-full border border-rw-blue/40 bg-rw-blue/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-rw-blue" data-testid="hero-sample-badge">
          Sample data
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {[
          { label: "Organic clicks", value: 48210, delta: "+38.4%" },
          { label: "Impressions", value: 1210000, compact: true, delta: "+52.1%" },
          { label: "Keywords in top 10", value: 214, delta: "+37" },
          { label: "Avg. position", value: 11.3, decimals: 1, delta: "↑ 6.2" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-rw-border bg-rw-bg2 p-3.5" data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-wider text-rw-dim">{s.label}</p>
            <p className="mt-1 font-mono text-lg sm:text-xl font-bold text-rw-text">
              {s.compact ? <><CountUp to={1.21} decimals={2} />M</> : <CountUp to={s.value} decimals={s.decimals || 0} />}
            </p>
            <p className="mt-0.5 inline-flex items-center gap-1 font-mono text-[10px] text-rw-green">
              <TrendingUp size={11} /> {s.delta}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-rw-border bg-rw-bg2 p-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider text-rw-dim">Organic traffic · 6 months</p>
          <p className="font-mono text-[10px] text-rw-green">▲ growing</p>
        </div>
        <svg viewBox="0 0 220 66" className="mt-2 w-full" aria-hidden="true">
          <defs>
            <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22C55E" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#22C55E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${SPARK} L220,66 L0,66 Z`} fill="url(#sparkfill)" />
          <motion.path d={SPARK} fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.8, ease: "easeOut" }} />
          <circle cx="220" cy="4" r="4" fill="#38BDF8" />
        </svg>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {[["LCP", "1.8s"], ["INP", "120ms"], ["CLS", "0.02"]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-rw-green/25 bg-rw-green/5 px-2 py-2 text-center" data-testid={`hero-vital-${k.toLowerCase()}`}>
            <p className="font-mono text-[9px] uppercase tracking-widest text-rw-dim">{k}</p>
            <p className="font-mono text-sm font-bold text-rw-green">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {KEYWORDS.map((k) => (
          <div key={k.kw} className="flex items-center justify-between rounded-lg border border-rw-border bg-rw-bg2 px-3 py-2">
            <span className="font-mono text-[11px] text-rw-muted">{k.kw}</span>
            <span className="inline-flex items-center gap-2 font-mono text-[11px]">
              <span className="text-rw-text">#{k.pos}</span>
              <span className={`inline-flex items-center ${k.delta >= 0 ? "text-rw-green" : "text-red-400"}`}>
                {k.delta >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{Math.abs(k.delta)}
              </span>
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-rw-dim">
        Illustrative sample data — not client results
      </p>
    </div>
  );
}

export function Hero() {
  const site = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const dashY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_75%_65%_at_50%_35%,black,transparent)]" aria-hidden="true" />
      <div className="absolute -top-40 left-1/2 h-96 w-[46rem] -translate-x-1/2 rounded-full bg-rw-green/10 blur-[120px]" aria-hidden="true" />

      <div className="container-rw relative grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.p
            className="eyebrow inline-flex items-center gap-2 rounded-full border border-rw-green/30 bg-rw-green/5 px-4 py-1.5"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-rw-green animate-pulse" /> SEO &amp; Web Development Specialists
          </motion.p>

          <h1 className="mt-7 font-jakarta font-extrabold tracking-tight text-rw-text text-4xl sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="hero-headline">
            <MaskedLine delay={0.15}>GET FOUND.</MaskedLine>
            <MaskedLine delay={0.3}>GET TRAFFIC.</MaskedLine>
            <MaskedLine delay={0.45}><span className="text-rw-green">GET CUSTOMERS.</span></MaskedLine>
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}>
            <p className="mt-6 font-jakarta text-lg sm:text-xl font-semibold text-rw-text" data-testid="hero-subheadline">
              SEO built to turn Google searches into measurable business opportunities.
            </p>
            <p className="mt-4 max-w-xl text-base text-rw-muted leading-relaxed" data-testid="hero-description">
              Ravi Works helps businesses improve organic search visibility, attract qualified traffic and generate
              better opportunities through technical SEO, on-page SEO, content SEO, local SEO, e-commerce SEO and
              ethical off-page SEO — plus search-friendly website development on Shopify, WordPress, WooCommerce
              and custom code.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3.5">
              <Link to="/contact" data-testid="hero-cta-audit"
                className="inline-flex items-center gap-2 rounded-full bg-rw-green px-7 py-3.5 text-sm font-semibold text-rw-bg transition-all duration-200 hover:bg-rw-green2 hover:-translate-y-0.5 glow-green">
                Get My SEO Audit <ArrowUpRight size={16} />
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-work"
                className="inline-flex items-center gap-2 rounded-full border border-rw-border2 px-7 py-3.5 text-sm font-semibold text-rw-text transition-all duration-200 hover:border-rw-green/60 hover:text-rw-green">
                View Our Work
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap gap-5 text-sm">
              <a href={site.waLink} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp"
                className="inline-flex items-center gap-2 text-rw-muted hover:text-rw-green transition-colors">
                <MessageCircle size={16} className="text-rw-green" /> WhatsApp Us
              </a>
              <a href={site.telLink} data-testid="hero-cta-call"
                className="inline-flex items-center gap-2 text-rw-muted hover:text-rw-blue transition-colors">
                <Phone size={16} className="text-rw-blue" /> Call Now
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div style={{ y: dashY }} initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}>
          <Dashboard />
        </motion.div>
      </div>
    </section>
  );
}
